import React, { useState, useEffect, useRef } from 'react';
import { tradeAPI } from '@/services/api';
import { useWebSocket } from '@/hooks/useWebSocket';
import type { ExecutionTapeEntry } from '@/types/bot';
import './ExecutionTape.css';

const MAX_ENTRIES = 200;

function formatTime(isoString: string): string {
  const d = new Date(isoString);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');
  const ms = String(d.getMilliseconds()).padStart(3, '0').slice(0, 2);
  return `${hh}:${mm}:${ss}.${ms}`;
}

export const ExecutionTape: React.FC = () => {
  const [entries, setEntries] = useState<ExecutionTapeEntry[]>([]);
  const bottomRef = useRef<HTMLDivElement>(null);
  const { subscribe, unsubscribe } = useWebSocket();

  // Load recent trades from API on mount
  useEffect(() => {
    tradeAPI.getRecent(50)
      .then((data) => setEntries(data))
      .catch((err) => console.warn('[ExecutionTape] Failed to load recent trades:', err.message));
  }, []);

  // Subscribe to TRADE messages via shared WebSocket
  useEffect(() => {
    const handler = (data: unknown) => {
      const trade = data as Record<string, unknown>;
      const entry: ExecutionTapeEntry = {
        id: (trade.id as string) || crypto.randomUUID(),
        time: (trade.time as string) || new Date().toISOString(),
        type: trade.type as ExecutionTapeEntry['type'],
        symbol: trade.symbol as string,
        qty: (trade.qty as number) || (trade.quantity as number) || 0,
        price: (trade.price as number) || 0,
        exchange: (trade.exchange as string) || '',
      };
      setEntries((prev) => {
        const next = [...prev, entry];
        return next.length > MAX_ENTRIES ? next.slice(next.length - MAX_ENTRIES) : next;
      });
    };

    subscribe('TRADE', handler);
    return () => unsubscribe('TRADE', handler);
  }, [subscribe, unsubscribe]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [entries.length]);

  return (
    <div className="execution-tape">
      <table className="execution-tape__table">
        <thead className="execution-tape__thead">
          <tr>
            <th className="execution-tape__th">Time</th>
            <th className="execution-tape__th">Type</th>
            <th className="execution-tape__th">Symbol</th>
            <th className="execution-tape__th execution-tape__th--right">Qty</th>
            <th className="execution-tape__th execution-tape__th--right">Price</th>
            <th className="execution-tape__th">Exchange</th>
          </tr>
        </thead>
        <tbody>
          {entries.length === 0 && (
            <tr>
              <td colSpan={6} className="execution-tape__td execution-tape__td--empty">
                Waiting for live executions...
              </td>
            </tr>
          )}
          {entries.map((entry) => (
            <tr key={entry.id} className="execution-tape__row">
              <td className="execution-tape__td execution-tape__td--time">
                {formatTime(entry.time)}
              </td>
              <td
                className={`execution-tape__td ${
                  entry.type === 'BUY'
                    ? 'execution-tape__td--type-buy'
                    : 'execution-tape__td--type-sell'
                }`}
              >
                {entry.type}
              </td>
              <td className="execution-tape__td execution-tape__td--symbol">{entry.symbol}</td>
              <td className="execution-tape__td execution-tape__td--qty">{entry.qty}</td>
              <td className="execution-tape__td execution-tape__td--price">
                {entry.price.toFixed(2)}
              </td>
              <td className="execution-tape__td execution-tape__td--exchange">{entry.exchange}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div ref={bottomRef} />
    </div>
  );
};
